import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import type { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { loginRequest, msalConfig, tokenRequest } from '../authConfig';
import { removeAllCookies } from '../Utils/cookieUtils';
import { getStoredToken, setStoredToken } from '../Utils/tokenStore';
import { getUserData, isTokenExpired } from '../Utils/tokenUtil';
import type { UserData } from '../Utils/tokenUtil';
import { notify } from '../lib/notify';

const LOGOUT_MARKER = 'app:just-logged-out';

/** MSAL's own key; it is written without the client id prefix. */
const INTERACTION_STATUS_KEY = 'msal.interaction.status';

const msalInstance = new PublicClientApplication(msalConfig);

/** MSAL must be initialised before any other call, and only once. */
let initialization: Promise<void> | null = null;

function ready(): Promise<void> {
  if (!initialization) {
    initialization = (async () => {
      clearLegacyAuthCookies();
      await msalInstance.initialize();
      // Resolves the hash left behind by a redirect (sign-out) navigation.
      const redirectResult = await msalInstance.handleRedirectPromise();
      const account = redirectResult?.account ?? msalInstance.getAllAccounts()[0];
      if (account && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(account);
      }
    })().catch(err => {
      // Let the next caller retry instead of caching the failure forever.
      initialization = null;
      throw err;
    });
  }
  return initialization;
}

/**
 * Cookies the previous build left behind: MSAL v1 cached itself in
 * `msal.<clientId>.*`, and the token used to be mirrored into `a_token`. Both
 * are dead weight the browser still sends on every request, and next to the
 * Authorization header they push past the gateway's 8 KB header limit, which
 * comes back as a bare 400. Drop them once on startup.
 */
function clearLegacyAuthCookies() {
  try {
    for (const entry of document.cookie.split(';')) {
      const name = (entry.split('=')[0] ?? '').trim();
      if (name !== 'a_token' && !name.startsWith('msal.')) continue;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  } catch (_) {
    /* empty */
  }
}

function activeAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/**
 * Persists the gateway access token. Only ever called with a result for
 * `tokenRequest` - the sign-in result carries a Graph token, which the gateway
 * would reject.
 */
function persist(result: AuthenticationResult): string | null {
  const token = result.accessToken || result.idToken;
  if (!token) return null;
  if (result.account) msalInstance.setActiveAccount(result.account);
  setStoredToken(token);
  return token;
}

/**
 * MSAL serialises silent requests internally, but both the auth context and
 * `App` ask on mount, so collapse concurrent callers onto one promise.
 */
let silentInFlight: Promise<string | null> | null = null;

function acquireSilently(): Promise<string | null> {
  if (!silentInFlight) {
    silentInFlight = (async () => {
      await ready();
      const account = activeAccount();
      if (!account) return null;
      return persist(await msalInstance.acquireTokenSilent({ ...tokenRequest, account }));
    })()
      .catch(err => {
        if (!(err instanceof InteractionRequiredAuthError)) {
          console.warn('[auth] silent token refresh failed', err);
        }
        return null;
      })
      .finally(() => {
        silentInFlight = null;
      });
  }
  return silentInFlight;
}

async function acquireApiToken({ allowPopup = false } = {}): Promise<string | null> {
  const silent = await acquireSilently();
  if (silent || !allowPopup) return silent;
  try {
    await ready();
    const account = activeAccount();
    return persist(
      await msalInstance.acquireTokenPopup({ ...tokenRequest, account: account ?? undefined })
    );
  } catch (err) {
    console.error('[auth] interactive token acquisition failed', err);
    if (hasErrorCode(err, BrowserAuthErrorCodes.interactionInProgress)) clearStaleInteraction();
    reportAuthFailure(err, 'Could not complete sign-in');
    return null;
  }
}

function hasErrorCode(err: unknown, code: string): boolean {
  return err instanceof BrowserAuthError && err.errorCode === code;
}

/**
 * A popup that never reaches the redirect page - dev server down, window killed
 * by the OS - leaves `interaction.status` behind in the cache. Every later
 * `loginPopup` then throws `interaction_in_progress`, so the sign-in button
 * appears to do nothing at all. Clear the flag so the retry can proceed.
 */
function clearStaleInteraction() {
  for (const store of [window.sessionStorage, window.localStorage]) {
    try {
      store.removeItem(INTERACTION_STATUS_KEY);
    } catch (_) {
      /* empty */
    }
  }
}

/** Turns an MSAL failure into something the user can act on. */
function reportAuthFailure(err: unknown, fallbackTitle: string) {
  // Closing the popup is a deliberate choice, not an error worth shouting about.
  if (hasErrorCode(err, BrowserAuthErrorCodes.userCancelled)) return;

  if (
    hasErrorCode(err, BrowserAuthErrorCodes.popupWindowError) ||
    hasErrorCode(err, BrowserAuthErrorCodes.emptyWindowError)
  ) {
    notify.error('Sign-in popup was blocked', {
      description: 'Allow popups for this site in your browser, then try again.',
    });
    return;
  }

  notify.fromError(err, { fallbackTitle });
}

const login = async (): Promise<UserData> => {
  try {
    await ready();
    let result;
    try {
      result = await msalInstance.loginPopup(loginRequest);
    } catch (err) {
      if (!hasErrorCode(err, BrowserAuthErrorCodes.interactionInProgress)) throw err;
      clearStaleInteraction();
      result = await msalInstance.loginPopup(loginRequest);
    }
    if (result.account) msalInstance.setActiveAccount(result.account);
    // Sign-in only establishes the session; the gateway token is a separate
    // resource and now comes from the refresh token cached above.
    const token = await acquireApiToken({ allowPopup: true });
    return token ? getUserData() : {};
  } catch (err) {
    console.error('[auth] sign-in failed', err);
    reportAuthFailure(err, 'Sign-in failed');
    return {};
  }
};

const logout = async () => {
  try {
    sessionStorage.setItem(LOGOUT_MARKER, '1');
  } catch (_) {
    /* empty */
  }
  clearAllCookies();
  try {
    await ready();
    await msalInstance.logoutRedirect({ account: activeAccount() ?? undefined });
  } catch (err) {
    console.error('[auth] sign-out failed', err);
    window.location.assign(`${window.location.origin}/dashboard`);
  }
};

const isUserAuthenticated = async (): Promise<UserData> => {
  const cached = getUserData();
  if (Object.keys(cached).length > 0) return cached;
  // The cookie is gone or expired, but MSAL may still hold a usable refresh
  // token from an earlier page load - spend it before forcing a sign-in popup.
  const token = await acquireApiToken({ allowPopup: false });
  return token ? getUserData() : {};
};

const fetchNewAccessToken = async ({ allowPopup = false } = {}): Promise<string | null> => {
  const cached = getStoredToken();
  if (!isTokenExpired(cached)) return cached;
  return acquireApiToken({ allowPopup });
};

export const clearAllCookies = () => {
  setStoredToken(null);
  try {
    removeAllCookies();
  } catch (err) {
    console.log(err);
  }
};

export const isJustLoggedOut = () => {
  try {
    const flag = sessionStorage.getItem(LOGOUT_MARKER);
    if (flag) {
      sessionStorage.removeItem(LOGOUT_MARKER);
      return true;
    }
  } catch (_) {
    /* empty */
  }
  return false;
};

const AuthService = {
  login,
  logout,
  isUserAuthenticated,
  fetchNewAccessToken,
  clearAllCookies,
  requireAccessToken: fetchNewAccessToken,
  isJustLoggedOut,
};

export default AuthService;
